import React from 'react';
import { FileTextIcon, QuoteIcon, UsersIcon } from 'lucide-react';
import { AudioTile } from './AudioTile';
import { StatusBadge } from '../ui/StatusBadge';
import { ARCHIVE_KIND_LABELS } from '../../data/archive';
import type { ArchiveItem } from '../../data/archive';
interface ArchiveTileProps {
  item: ArchiveItem;
  onOpen: (item: ArchiveItem) => void;
}

/**
 * One archive entry. Each kind is deliberately a different object — a
 * photograph, a video, a quotation, a scanned artifact, a dated line of record
 * — so the grid reads as an archive rather than a photo wall.
 */
export function ArchiveTile({
  item,
  onOpen
}: ArchiveTileProps) {
  const kindLabel = ARCHIVE_KIND_LABELS[item.kind];
  if (item.kind === 'audio') {
    return <AudioTile item={item} onOpen={onOpen} data-id="element-63" data-mp="d4d4kn3dx3i9" />;
  }
  if (item.kind === 'memory') {
    return <Shell item={item} onOpen={onOpen} className="bg-canvas p-5" data-id="element-64" data-mp="22jaxzmf1li9u">
        <QuoteIcon aria-hidden="true" className="h-5 w-5 text-ink-subtle" data-id="element-65" data-mp="27i4bheubx9a0" />
        <p className="mt-3 text-body italic text-ink" data-id="element-66" data-mp="wp7utockk7ik">“{item.quote}”</p>
        <p className="mt-4 flex flex-wrap items-center gap-2 text-small text-ink-muted" data-id="element-67" data-mp="wn4yynoqjk5x">
          {item.credit} · {item.year}
          {item.mine && item.status ? <StatusBadge status={item.status} data-id="element-68" data-mp="16fas40qq47mp" /> : null}
        </p>
      </Shell>;
  }
  if (item.kind === 'record') {
    return <Shell item={item} onOpen={onOpen} className="bg-card p-5" data-id="element-69" data-mp="11gpy4j22ek6p">
        <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-70" data-mp="drevew15mfpx">
          <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-71" data-mp="9q3c188viqq6" />
          {item.year} · {kindLabel}
        </span>
        <p className="mt-2 text-heading font-semibold text-ink" data-id="element-72" data-mp="npuo33ftxx1j">{item.title}</p>
        {item.note ? <p className="mt-1.5 text-small text-ink-muted" data-id="element-73" data-mp="io595cphkz86">{item.note}</p> : null}
        <p className="mt-4 text-small text-ink-muted" data-id="element-74" data-mp="1z2eyoa5gsu0l">{item.credit}</p>
      </Shell>;
  }
  return <Shell item={item} onOpen={onOpen} className="bg-card" data-id="element-75" data-mp="1u3txo8ie26b3">
      <div className="relative" data-id="element-76" data-mp="225l0p8pysoz9">
        {item.image ? <img src={item.image.src} alt={item.image.alt} className={`w-full object-cover ${item.kind === 'artifact' ? 'h-56 bg-canvas object-contain p-3' : 'h-56'}`} data-id="element-77" data-mp="2251h7nko1sj4" /> : null}

        {item.kind === 'artifact' ? <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-pill bg-card px-2.5 py-1 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-78" data-mp="2g4ay6pgp4qo5">
            <FileTextIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-79" data-mp="1nh6o462qxm5i" />
            {kindLabel}
          </span> : null}
      </div>

      <div className="flex flex-col gap-1 p-4" data-id="element-80" data-mp="1sdovldpgnylj">
        <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-81" data-mp="1x79quxzo6eh1">
          {item.year} · {kindLabel}
        </span>
        <p className="text-body font-medium text-ink" data-id="element-82" data-mp="zoq8e26he69d">{item.title}</p>
        <p className="flex flex-wrap items-center gap-2 text-small text-ink-muted" data-id="element-83" data-mp="14omafvvmbnma">
          {item.credit}
          {item.mine && item.status ? <StatusBadge status={item.status} data-id="element-84" data-mp="x41zm9h52t9a" /> : null}
        </p>

        {item.unidentified ? <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-pill bg-primary-soft px-2.5 py-1 text-small font-medium text-primary-text" data-id="element-85" data-mp="s5h1cflg6jnh">
            <UsersIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-86" data-mp="i8c8xy10dry8" />
            {item.unidentified} unnamed
          </span> : null}
      </div>
    </Shell>;
}
function Shell({
  item,
  onOpen,
  className = '',
  children
}: ArchiveTileProps & {
  className?: string;
  children: React.ReactNode;
}) {
  return <li className="break-inside-avoid" data-id="element-87" data-mp="4i0evtkv5j4g">
      <button type="button" onClick={() => onOpen(item)} className={`w-full overflow-hidden rounded-card border border-rule text-left transition-colors hover:border-ink-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${className}`} data-id="element-88" data-mp="3voaa8f3a6kbb">
        {children}
      </button>
    </li>;
}